import { useEffect } from 'react';
import { List } from 'immutable';
import { useStudentNotes } from '../../hooks';
import { GradientLoadingIndicator } from 'edrolo-style-guide';
import { STATUS as BY_SCHOOL_CLASS_STATUS } from 'edrolo/domains/bySchoolClass';
import { STATUS as LESSON_STATUS } from 'edrolo/domains/lessons';
import NotebookPageHeaderStudentView from './NotebookPageHeaderStudentView';
import NotebookItem from './NotebookItem';
import NotebookPageTopBar from './NotebookPageTopBar';
import AppContainer from 'edrolo/domains/app/components/AppContainer';
import urlResolver from 'edrolo/utils/URLResolver';
import NotebookPageHeaderTeacherView from './NotebookPageHeaderTeacherView';
import { Lesson } from '../../types/Lesson';
import { Chunk } from '../../types/Chunk';
import {
  PrevAndNextLessonIds,
  PrevAndNextProfileIds,
  StudentNote,
} from '../../types/Notebook';

const NotebookPage = ({
  classId,
  lessonId,
  classCode,
  classDetailStatus,
  courseAssemblageStatus,
  lesson,
  lessonStatus,
  prevAndNextLessonIds,
  loadClassDetail,
  loadCourseAssemblage,
  loadLesson,
  isTeacherView,
  profileId,
  currentStudentName,
  prevAndNextStudentProfileIds,
}: NotebookPageProps): JSX.Element | null => {
  const { studentNotesData, studentNotesIsLoading, studentNotesError } =
    useStudentNotes(classId, profileId);

  useEffect(() => {
    loadClassDetail(classId);
    loadCourseAssemblage(classId);
    loadLesson(lessonId);
  }, [classId, lessonId, loadClassDetail, loadCourseAssemblage, loadLesson]);

  if (studentNotesError) {
    return null;
  }

  const isLoading =
    studentNotesIsLoading ||
    lessonStatus === LESSON_STATUS.LOADING ||
    classDetailStatus === BY_SCHOOL_CLASS_STATUS.LOADING ||
    courseAssemblageStatus === BY_SCHOOL_CLASS_STATUS.LOADING;

  if (isLoading) {
    return <GradientLoadingIndicator />;
  }

  const previousLessonId = prevAndNextLessonIds.get('prevLessonId');
  const nextLessonId = prevAndNextLessonIds.get('nextLessonId');
  const previousLessonLink = previousLessonId
    ? urlResolver.reverse('student-notes-notebook', {
        classId,
        lessonId: previousLessonId,
        profileId,
      })
    : '';
  const nextLessonLink = nextLessonId
    ? urlResolver.reverse('student-notes-notebook', {
        classId,
        lessonId: nextLessonId,
        profileId,
      })
    : '';

  const previousStudentProfileId = prevAndNextStudentProfileIds.get(
    'prevStudentProfileId'
  );
  const nextStudentProfileId = prevAndNextStudentProfileIds.get(
    'nextStudentProfileId'
  );
  const previousStudentLink = previousStudentProfileId
    ? urlResolver.reverse('student-notes-notebook', {
        classId,
        lessonId,
        profileId: previousStudentProfileId,
      })
    : '';
  const nextStudentLink = nextStudentProfileId
    ? urlResolver.reverse('student-notes-notebook', {
        classId,
        lessonId,
        profileId: nextStudentProfileId,
      })
    : '';

  return (
    <AppContainer
      renderTopBar={() => (
        <NotebookPageTopBar
          classId={classId}
          lesson={lesson}
          isTeacherView={isTeacherView}
        />
      )}
    >
      {isTeacherView ? (
        <NotebookPageHeaderTeacherView
          lessonTitle={lesson.get('title') as unknown as string}
          classCode={classCode}
          previousLessonLink={previousLessonLink}
          nextLessonLink={nextLessonLink}
          previousStudentLink={previousStudentLink}
          nextStudentLink={nextStudentLink}
          currentStudentName={currentStudentName}
        />
      ) : (
        <NotebookPageHeaderStudentView
          lessonTitle={lesson.get('title') as unknown as string}
          classCode={classCode}
          previousLessonLink={previousLessonLink}
          nextLessonLink={nextLessonLink}
        />
      )}
      {(lesson.get('chunks') as unknown as List<Chunk>)?.map(
        // immutablejs 3.x types are broken and make all args optional
        // instead of unioning different function sigs
        (chunk: Chunk | undefined) => {
          if (!chunk) {
            return null;
          }

          const chunkNote = studentNotesData?.find(
            (note: StudentNote) =>
              note['chunk_id'] === (chunk.get('id') as unknown as number)
          );

          return (
            <NotebookItem
              key={chunk.get('id') as unknown as number}
              chunkTitle={chunk.get('title') as unknown as string}
              chunkNote={(chunkNote && chunkNote['note_rendered']) || ''}
              classId={classId}
              lessonId={lessonId}
              chunkIndex={chunk.get('index') as unknown as number}
              chunkId={chunk.get('id') as unknown as number}
            />
          );
        }
      )}
    </AppContainer>
  );
};

export default NotebookPage;

export interface NotebookPageProps {
  classId: ClassId;
  lessonId: string;
  classCode: string;
  classDetailStatus: string;
  courseAssemblageStatus: string;
  lesson: Lesson;
  lessonStatus: string;
  prevAndNextLessonIds: PrevAndNextLessonIds;
  loadClassDetail: (classId: ClassId) => void;
  loadCourseAssemblage: (classId: ClassId) => void;
  loadLesson: (lessonId: string) => void;
  isTeacherView: boolean;
  profileId: string;
  currentStudentName: string;
  prevAndNextStudentProfileIds: PrevAndNextProfileIds;
}
